import "./VideoBlock.scss";
import { useRef, useState, useEffect } from "react";
import {
  BlockTop,
  GridPresetWideContent,
  GridRow,
  VideoItem,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
} from "/App.Components";
import { VideoBlockProps } from "/Models/Generated/VideoBlockProps";
import { createSpreadableBem } from "/Helpers/createBem";

const VideoBlock = ({
  blockTop,
  videoList,
  scrollLeftAriaLabel = "Skyv til venstre",
  scrollRightAriaLabel = "Skyv til høyre",
}: VideoBlockProps) => {
  const bem = createSpreadableBem("video-block-norad");

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Start with no active video until videoList is ready
  const [activeVideoId, setActiveVideoId] = useState<number | null>(null);

  useEffect(() => {
    if (videoList && videoList.length > 0 && activeVideoId === null) {
      setActiveVideoId(0);
    }
  }, [videoList, activeVideoId]);

  const getScrollAmount = () => {
    if (!containerRef.current) return 0;
    const firstChild = containerRef.current.children[0] as HTMLElement;
    if (!firstChild) return 0;
    const itemWidth = firstChild.offsetWidth;
    const gap = 12;
    return itemWidth + gap;
  };

  const handleScroll = (direction: "left" | "right") => {
    if (!containerRef.current) return;
    const amount = getScrollAmount();
    containerRef.current.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const checkScroll = () => {
    if (!containerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    requestAnimationFrame(() => checkScroll());

    const handleResize = () => checkScroll();
    container.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div {...bem("")}>
      <GridRow>
        <GridPresetWideContent>
          <BlockTop {...blockTop} externalArrowIcon={true} />
          <div {...bem("__button-container")}>
            <button
              onClick={() => handleScroll("left")}
              disabled={!canScrollLeft}
            >
              <CaretCircleLeftIcon
                size="32"
                thickness="Light"
                color="#5B0337"
                ariaLabel={scrollLeftAriaLabel}
              />
            </button>
            <button
              onClick={() => handleScroll("right")}
              disabled={!canScrollRight}
            >
              <CaretCircleRightIcon
                size="32"
                thickness="Light"
                color="#5B0337"
                ariaLabel={scrollRightAriaLabel}
              />
            </button>
          </div>
        </GridPresetWideContent>
      </GridRow>
      <GridRow>
        <div {...bem("__video-container")} ref={containerRef}>
          {videoList &&
            activeVideoId !== null &&
            videoList.map((video, index) => (
              <VideoItem
                key={index}
                {...video}
                id={index}
                activeVideoId={activeVideoId}
                setActiveVideoId={setActiveVideoId}
              />
            ))}
        </div>
      </GridRow>
    </div>
  );
};

export default VideoBlock;
